import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { labLabel, labCanonical } from "@/lib/labI18n";
import { useAuth } from "@/contexts/AuthContext";
import { setActiveClinicId } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Buildings, Users as UsersIcon, ListChecks, Flask, Plus, Trash, Upload, PawPrint, Pencil, UserPlus, Check, X } from "@phosphor-icons/react";

export default function SettingsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="animate-fade-in-up">
      <h1 className="text-3xl font-display font-extrabold text-pf-text mb-6">{t("settings")}</h1>

      <Tabs defaultValue="clinic">
        <TabsList className="bg-white border border-pf-border rounded-lg p-1 flex-wrap h-auto">
          <TabsTrigger value="clinic" data-testid="tab-clinic"><Buildings size={14} className="mr-1" />{t("clinicProfile")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon size={14} className="mr-1" />{t("users")}</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="pending" data-testid="tab-pending"><UserPlus size={14} className="mr-1" />{t("pendingUsers")}</TabsTrigger>
          )}
          <TabsTrigger value="reasons" data-testid="tab-reasons"><ListChecks size={14} className="mr-1" />{t("customVisitReasons")}</TabsTrigger>
          <TabsTrigger value="species" data-testid="tab-species"><PawPrint size={14} className="mr-1" />{t("speciesCatalog")}</TabsTrigger>
          <TabsTrigger value="ranges" data-testid="tab-ranges"><Flask size={14} className="mr-1" />{t("referenceRanges")}</TabsTrigger>
        </TabsList>

        <TabsContent value="clinic" className="mt-4"><ClinicSection isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersSection isAdmin={isAdmin} /></TabsContent>
        {isAdmin && (
          <TabsContent value="pending" className="mt-4"><PendingUsersSection /></TabsContent>
        )}
        <TabsContent value="reasons" className="mt-4"><ReasonsSection isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="species" className="mt-4"><SpeciesSection /></TabsContent>
        <TabsContent value="ranges" className="mt-4"><RangesSection isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ClinicSection({ isAdmin }) {
  const { t } = useLang();
  const [clinic, setClinic] = useState(null);
  const [busy, setBusy] = useState(false);
  const [myClinics, setMyClinics] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newClinic, setNewClinic] = useState({ name: "", email: "", phone: "", address: "" });

  async function loadClinics() {
    try {
      const [cur, mine] = await Promise.all([api.get("/clinic"), api.get("/my/clinics")]);
      setClinic(cur.data);
      setMyClinics(mine.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    loadClinics();
  }, []);

  async function switchTo(id) {
    if (id === clinic?.id) return;
    try {
      await api.post("/my/clinics/switch", { clinic_id: id });
      setActiveClinicId(id);
      toast.success(t("saved"));
      // Full reload so all pages re-fetch clinic-scoped data
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function removeClinic(cl) {
    if (myClinics.length <= 1) {
      toast.error(t("cantDeleteLastClinic"));
      return;
    }
    if (!window.confirm(`${t("deleteClinicConfirm")} "${cl.name}"?\n\n${t("deleteClinicWarn")}`)) return;
    try {
      await api.delete(`/my/clinics/${cl.id}`);
      if (cl.id === clinic?.id) {
        const fallback = myClinics.find((item) => item.id !== cl.id);
        setActiveClinicId(fallback?.id);
      }
      toast.success(t("saved"));
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function createClinic() {
    if (!newClinic.name.trim()) {
      toast.error(t("required") || "Required");
      return;
    }
    try {
      const { data } = await api.post("/my/clinics", newClinic);
      setActiveClinicId(data.id);
      toast.success(t("saved"));
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch("/clinic", {
        name: clinic.name,
        email: clinic.email,
        phone: clinic.phone,
        address: clinic.address,
        logo_base64: clinic.logo_base64,
        cui: clinic.cui,
        reg_com: clinic.reg_com,
        iban: clinic.iban,
        bank_name: clinic.bank_name,
        invoice_series: clinic.invoice_series,
      });
      setClinic(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  function onLogoFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setClinic((c) => ({ ...c, logo_base64: reader.result }));
    };
    reader.readAsDataURL(f);
  }

  if (!clinic) return <div className="text-sm text-pf-text-secondary">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Multi-clinic switcher */}
      <div className="pf-card p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="pf-section-title">{t("myClinics")}</div>
          {isAdmin && (
            <button
              onClick={() => setShowAdd((s) => !s)}
              className="pf-btn-ghost"
              data-testid="clinic-add-toggle"
            >
              <Plus size={14} weight="bold" /> {t("addClinic")}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {myClinics.map((c) => (
            <div
              key={c.id}
              data-testid={`clinic-chip-${c.id}`}
              className={`inline-flex items-center rounded-lg border text-sm font-medium transition-all overflow-hidden ${
                c.is_current
                  ? "border-pf-primary bg-pf-primary text-white shadow-card"
                  : "border-pf-border bg-white text-pf-text"
              }`}
            >
              <button
                onClick={() => switchTo(c.id)}
                className={`px-3 py-2 ${c.is_current ? "" : "hover:bg-pf-bg"}`}
              >
                {c.name || "—"}
                {c.is_current && <span className="ml-2 text-[10px] opacity-80">✓</span>}
              </button>
              {isAdmin && myClinics.length > 1 && (
                <button
                  onClick={() => removeClinic(c)}
                  data-testid={`clinic-delete-${c.id}`}
                  className={`px-2 py-2 border-l ${
                    c.is_current
                      ? "border-white/30 text-white/80 hover:bg-white/10"
                      : "border-pf-border text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg"
                  }`}
                  title={t("deleteClinic")}
                >
                  <Trash size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
        {showAdd && (
          <div className="mt-4 pt-4 border-t border-pf-border grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              className="pf-input sm:col-span-2"
              placeholder={t("clinicName") || t("name")}
              value={newClinic.name}
              onChange={(e) => setNewClinic({ ...newClinic, name: e.target.value })}
              data-testid="new-clinic-name"
            />
            <input
              className="pf-input"
              placeholder={t("email")}
              type="email"
              value={newClinic.email}
              onChange={(e) => setNewClinic({ ...newClinic, email: e.target.value })}
            />
            <input
              className="pf-input"
              placeholder={t("phone")}
              value={newClinic.phone}
              onChange={(e) => setNewClinic({ ...newClinic, phone: e.target.value })}
            />
            <input
              className="pf-input sm:col-span-2"
              placeholder={t("address")}
              value={newClinic.address}
              onChange={(e) => setNewClinic({ ...newClinic, address: e.target.value })}
            />
            <div className="sm:col-span-2 flex justify-end">
              <button onClick={createClinic} className="pf-btn-primary" data-testid="create-clinic-btn">
                <Plus size={14} weight="bold" /> {t("createClinic") || t("addClinic")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pf-card p-4 sm:p-6">
      <div className="flex items-center gap-5 mb-6">
        <div className="h-20 w-20 rounded-xl border border-pf-border overflow-hidden bg-pf-bg grid place-items-center">
          {clinic.logo_base64 ? (
            <img src={clinic.logo_base64} alt="Clinic logo" className="w-full h-full object-cover" />
          ) : (
            <Buildings size={28} className="text-pf-text-secondary" />
          )}
        </div>
        <div>
          <label className={`pf-btn-ghost cursor-pointer ${isAdmin ? "" : "opacity-50 pointer-events-none"}`}>
            <Upload size={14} />
            {clinic.logo_base64 ? t("replaceLogo") : t("uploadLogo")}
            <input type="file" accept="image/*" hidden onChange={onLogoFile} data-testid="clinic-logo-input" />
          </label>
          <div className="text-xs text-pf-text-secondary mt-1">PNG/JPG, up to 2MB.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="pf-label mb-1 block">{t("name")}</label>
          <input data-testid="clinic-name-input" disabled={!isAdmin} className="pf-input" value={clinic.name || ""} onChange={(e) => setClinic({ ...clinic, name: e.target.value })} />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("email")}</label>
          <input disabled={!isAdmin} className="pf-input" value={clinic.email || ""} onChange={(e) => setClinic({ ...clinic, email: e.target.value })} />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("phone")}</label>
          <input disabled={!isAdmin} className="pf-input" value={clinic.phone || ""} onChange={(e) => setClinic({ ...clinic, phone: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className="pf-label mb-1 block">{t("address")}</label>
          <input disabled={!isAdmin} className="pf-input" value={clinic.address || ""} onChange={(e) => setClinic({ ...clinic, address: e.target.value })} />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("cui")}</label>
          <input data-testid="clinic-cui-input" disabled={!isAdmin} className="pf-input" value={clinic.cui || ""} onChange={(e) => setClinic({ ...clinic, cui: e.target.value })} placeholder="RO40123456" />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("regCom")}</label>
          <input data-testid="clinic-regcom-input" disabled={!isAdmin} className="pf-input" value={clinic.reg_com || ""} onChange={(e) => setClinic({ ...clinic, reg_com: e.target.value })} placeholder="J29/123/2024" />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("iban")}</label>
          <input data-testid="clinic-iban-input" disabled={!isAdmin} className="pf-input" value={clinic.iban || ""} onChange={(e) => setClinic({ ...clinic, iban: e.target.value })} placeholder="RO49…" />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("bankName")}</label>
          <input data-testid="clinic-bank-input" disabled={!isAdmin} className="pf-input" value={clinic.bank_name || ""} onChange={(e) => setClinic({ ...clinic, bank_name: e.target.value })} placeholder="Banca Transilvania" />
        </div>
        <div>
          <label className="pf-label mb-1 block">{t("invoiceSeries")}</label>
          <input data-testid="clinic-invseries-input" disabled={!isAdmin} className="pf-input" value={clinic.invoice_series || ""} onChange={(e) => setClinic({ ...clinic, invoice_series: e.target.value.toUpperCase() })} placeholder="INV" />
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end mt-6">
          <button data-testid="save-clinic-btn" onClick={save} disabled={busy} className="pf-btn-primary">
            {busy ? t("saving") : t("saveClinicInfo")}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

function UsersSection({ isAdmin }) {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "veterinarian" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // user object

  async function load() {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(t("saved"));
      setForm({ email: "", password: "", name: "", role: "veterinarian" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function del(id) {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveEdit() {
    if (!editing) return;
    const body = {
      name: editing.name,
      email: editing.email,
      role: editing.role,
    };
    if (editing._newPassword && editing._newPassword.trim().length >= 6) {
      body.password = editing._newPassword;
    }
    try {
      await api.patch(`/users/${editing.id}`, body);
      toast.success(t("saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 pf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-pf-bg text-pf-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3">{t("name")}</th>
              <th className="text-left font-semibold px-4 py-3">{t("email")}</th>
              <th className="text-left font-semibold px-4 py-3">{t("role")}</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-pf-border" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-pf-text-secondary">{u.email}</td>
                <td className="px-4 py-3">
                  {u.role === "admin" && t("admin")}
                  {u.role === "veterinarian" && t("veterinarianRole")}
                  {u.role === "assistant" && t("assistant")}
                  {u.role && !["admin","veterinarian","assistant"].includes(u.role) && u.role}
                </td>
                <td className="px-4 py-3 text-right">
                  {isAdmin && (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => setEditing({ ...u, _newPassword: "" })}
                        className="p-1.5 rounded text-pf-text-secondary hover:text-pf-primary hover:bg-pf-primary-light"
                        data-testid={`user-edit-${u.id}`}
                        title={t("editUser")}
                      >
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => del(u.id)} className="p-1.5 rounded text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg" data-testid={`user-delete-${u.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isAdmin && (
        <div className="pf-card p-5">
          <div className="pf-section-title mb-3">{t("createUser")}</div>
          <div className="space-y-3">
            <input data-testid="new-user-name" className="pf-input" placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="new-user-email" className="pf-input" placeholder={t("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="new-user-password" className="pf-input" placeholder={t("password")} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <select data-testid="new-user-role" className="pf-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="veterinarian">{t("veterinarianRole")}</option>
              <option value="assistant">{t("assistant")}</option>
            </select>
            <button data-testid="create-user-btn" onClick={create} disabled={busy} className="pf-btn-primary w-full justify-center">
              <Plus size={14} weight="bold" /> {t("createUser")}
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("editUser")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="pf-label mb-1 block">{t("name")}</label>
                <input className="pf-input" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" />
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("email")}</label>
                <input className="pf-input" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="edit-user-email" />
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("role")}</label>
                <select className="pf-input" value={editing.role || "veterinarian"} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="edit-user-role">
                  <option value="veterinarian">{t("veterinarianRole")}</option>
                  <option value="assistant">{t("assistant")}</option>
                  {editing.role === "admin" && <option value="admin">{t("admin")}</option>}
                </select>
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("newPassword")}</label>
                <input className="pf-input" type="password" value={editing._newPassword || ""} onChange={(e) => setEditing({ ...editing, _newPassword: e.target.value })} data-testid="edit-user-password" placeholder="••••••" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button className="pf-btn-ghost" onClick={() => setEditing(null)}>{t("cancel")}</button>
                <button className="pf-btn-primary" onClick={saveEdit} data-testid="edit-user-save">{t("save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReasonsSection({ isAdmin }) {
  const { t } = useLang();
  const [reasons, setReasons] = useState([]);
  const [label, setLabel] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/visit-reasons");
      setReasons(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!label.trim()) return;
    try {
      await api.post("/visit-reasons", { label: label.trim() });
      setLabel("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function remove(id) {
    try {
      await api.delete(`/visit-reasons/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="pf-card p-5 max-w-2xl">
      <div className="flex flex-wrap gap-2 mb-4">
        {reasons.length === 0 && <div className="text-sm text-pf-text-secondary">No reasons yet.</div>}
        {reasons.map((r) => (
          <span key={r.id} className="pf-chip !py-1.5 pr-1">
            {r.label}
            {isAdmin && (
              <button onClick={() => remove(r.id)} className="ml-1 p-0.5 rounded hover:text-pf-danger">
                <Trash size={12} />
              </button>
            )}
          </span>
        ))}
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <input data-testid="new-reason-input" className="pf-input flex-1" placeholder="e.g. Vaccination" value={label} onChange={(e) => setLabel(e.target.value)} />
          <button data-testid="add-reason-btn" onClick={add} className="pf-btn-primary">
            <Plus size={14} weight="bold" /> {t("addReason")}
          </button>
        </div>
      )}
    </div>
  );
}

function SpeciesSection() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/species");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function add() {
    const n = name.trim();
    if (!n) return;
    try {
      await api.post("/species", { name: n });
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function remove(id) {
    if (!window.confirm(t("delete") + "?")) return;
    try {
      await api.delete(`/species/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="pf-card p-5 max-w-2xl">
      <div className="flex flex-wrap gap-2 mb-4">
        {items.length === 0 && <div className="text-sm text-pf-text-secondary">—</div>}
        {items.map((s) => (
          <span key={s.id} className="pf-chip !py-1.5 pr-1 capitalize" data-testid={`species-chip-${s.name.toLowerCase()}`}>
            {s.name === "dog" ? t("dog") : s.name === "cat" ? t("cat") : s.name}
            <button onClick={() => remove(s.id)} className="ml-1 p-0.5 rounded hover:text-pf-danger">
              <Trash size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid="new-species-input"
          className="pf-input flex-1"
          placeholder={t("speciesName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button data-testid="add-species-btn" onClick={add} className="pf-btn-primary">
          <Plus size={14} weight="bold" /> {t("addSpecies")}
        </button>
      </div>
    </div>
  );
}

function RangesSection({ isAdmin }) {
  const { t, lang } = useLang();
  const [species, setSpecies] = useState("dog");
  const [speciesList, setSpeciesList] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/species").then((r) => setSpeciesList(r.data)).catch(() => {});
  }, []);

  async function load() {
    try {
      const { data } = await api.get(`/reference-ranges/${species}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [species]);

  function upd(idx, k, v) {
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  }

  function add(category) {
    setItems((rows) => [...rows, { parameter: "", unit: "", min_value: null, max_value: null, category }]);
  }

  function remove(idx) {
    setItems((rows) => rows.filter((_, i) => i !== idx));
  }

  async function save() {
    try {
      await api.put(`/reference-ranges/${species}`, { species, items });
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const bio = items.filter((i) => i.category === "biochemistry");
  const cbc = items.filter((i) => i.category === "cbc" || i.category === "hematology");
  const electrolytes = items.filter((i) => i.category === "electrolytes");
  const coag = items.filter((i) => i.category === "coagulation");
  const immuno = items.filter((i) => i.category === "immunology");

  function renderTable(category, list, label, opts = {}) {
    // Sediment urinar has a fixed predefined parameter set — never allow
    // adding/removing rows in Settings.
    const locked = opts.locked === true;
    return (
      <div className="mb-6" data-testid={`range-section-${category}`}>
        <div className="pf-section-title mb-2">{label}</div>
        <div className="overflow-x-auto pf-card p-3">
          <table className="w-full text-sm">
            <thead className="text-pf-text-secondary text-xs uppercase">
              <tr>
                <th className="text-left px-2 py-1">{t("parameter")}</th>
                <th className="text-left px-2 py-1 w-24">{t("unit")}</th>
                <th className="text-left px-2 py-1 w-24">{t("minValue")}</th>
                <th className="text-left px-2 py-1 w-24">{t("maxValue")}</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((it, idx) => {
                const globalIdx = items.indexOf(it);
                return (
                  <tr key={globalIdx}>
                    <td className="px-2 py-1">
                      <input
                        disabled={!isAdmin || locked}
                        data-testid={`range-param-${category}-${idx}`}
                        className="pf-input"
                        value={labLabel(it.parameter || "", lang)}
                        onChange={(e) => upd(globalIdx, "parameter", labCanonical(e.target.value) || e.target.value)}
                      />
                    </td>
                    <td className="px-2 py-1"><input disabled={!isAdmin} className="pf-input" value={it.unit || ""} onChange={(e) => upd(globalIdx, "unit", e.target.value)} /></td>
                    <td className="px-2 py-1"><input disabled={!isAdmin} inputMode="decimal" className="pf-input" value={it.min_value ?? ""} onChange={(e) => { const raw = e.target.value.replace(",", "."); upd(globalIdx, "min_value", raw === "" ? null : Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : it.min_value); }} /></td>
                    <td className="px-2 py-1"><input disabled={!isAdmin} inputMode="decimal" className="pf-input" value={it.max_value ?? ""} onChange={(e) => { const raw = e.target.value.replace(",", "."); upd(globalIdx, "max_value", raw === "" ? null : Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : it.max_value); }} /></td>
                    <td className="px-2 py-1 text-right">
                      {isAdmin && !locked && (
                        <button onClick={() => remove(globalIdx)} className="p-1.5 rounded text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg">
                          <Trash size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isAdmin && !locked && (
            <button onClick={() => add(category)} data-testid={`range-add-${category}`} className="mt-2 inline-flex items-center gap-1 text-sm text-pf-primary hover:underline">
              <Plus size={14} weight="bold" /> {t("addRow")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="pf-label">{t("species")}:</label>
        <select
          data-testid="species-select"
          className="pf-input max-w-[240px]"
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
        >
          {speciesList.length === 0 ? (
            <>
              <option value="dog">{t("dog")}</option>
              <option value="cat">{t("cat")}</option>
            </>
          ) : (
            speciesList.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name === "dog" ? t("dog") : s.name === "cat" ? t("cat") : s.name}
              </option>
            ))
          )}
        </select>
      </div>
      {renderTable("biochemistry", bio, t("biochemistry"))}
      {renderTable("hematology", cbc, t("parHematologie") || t("cbc"))}
      {renderTable("electrolytes", electrolytes, t("parElectroliti"))}
      {renderTable("coagulation", coag, t("parTesteCoagulare"))}
      {renderTable("immunology", immuno, t("parImunologie"))}
      {isAdmin && (
        <div className="flex justify-end">
          <button data-testid="save-ranges-btn" onClick={save} className="pf-btn-primary">
            {t("saveRefRanges")}
          </button>
        </div>
      )}
    </div>
  );
}


function PendingUsersSection() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null); // user being approved
  const [approveForm, setApproveForm] = useState({ role: "veterinarian", clinic_id: "" });

  async function refresh() {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/admin/pending-users"),
        api.get("/my/clinics"),
      ]);
      setRows(pRes.data || []);
      setClinics(cRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function approve() {
    if (!target || !approveForm.clinic_id) {
      toast.error(t("selectClinic"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/pending-users/${target.id}/approve`, approveForm);
      toast.success(t("userApproved"));
      setTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function reject(u) {
    if (!confirm(t("confirmReject"))) return;
    try {
      await api.post(`/admin/pending-users/${u.id}/reject`, { reason: "" });
      toast.success(t("userRejected"));
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="pf-card p-6">
      <h2 className="font-display text-lg font-bold text-pf-text mb-4">{t("pendingUsers")}</h2>
      {rows.length === 0 ? (
        <div className="text-sm text-pf-text-secondary">{t("noPendingUsers")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-pf-text-secondary text-xs uppercase tracking-wide">
                <th className="px-4 py-2">{t("fullName")}</th>
                <th className="px-4 py-2">{t("email")}</th>
                <th className="px-4 py-2 text-right">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-t border-pf-border" data-testid={`pending-user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-pf-text-secondary">{u.email}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => {
                          setTarget(u);
                          setApproveForm({ role: "veterinarian", clinic_id: clinics[0]?.id || "" });
                        }}
                        className="pf-btn-primary text-xs px-2 py-1"
                        data-testid={`pending-approve-${u.id}`}
                      >
                        <Check size={14} /> {t("approve")}
                      </button>
                      <button
                        onClick={() => reject(u)}
                        className="p-1.5 rounded text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg"
                        data-testid={`pending-reject-${u.id}`}
                        title={t("reject")}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(open) => !open && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{t("approveUser")}</DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="text-sm text-pf-text-secondary">
                <div><strong>{target.name}</strong></div>
                <div>{target.email}</div>
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("role")}</label>
                <select className="pf-input" value={approveForm.role} onChange={(e) => setApproveForm({ ...approveForm, role: e.target.value })} data-testid="approve-role">
                  <option value="veterinarian">{t("veterinarianRole")}</option>
                  <option value="assistant">{t("assistant")}</option>
                </select>
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("clinic")}</label>
                <select className="pf-input" value={approveForm.clinic_id} onChange={(e) => setApproveForm({ ...approveForm, clinic_id: e.target.value })} data-testid="approve-clinic">
                  <option value="">—</option>
                  {clinics.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setTarget(null)} className="pf-btn-ghost">{t("cancel")}</button>
                <button onClick={approve} disabled={busy || !approveForm.clinic_id} className="pf-btn-primary" data-testid="approve-confirm">
                  {busy ? t("saving") : t("approve")}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
